import { Box, Button, Grid, Stack, Typography, Tooltip, IconButton, Divider } from '@mui/material';
import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import { useTranslation } from 'react-i18next';
import ImageCrop from './ImageCrop';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { isNil } from 'lodash';
import imageCompression from 'browser-image-compression';
import { useLoading } from 'hooks/useLoading';
import { imgSizesCalc } from 'utils';
import { useSnackbar } from 'notistack';

const FileExplorerUploadTab = forwardRef((props, ref) => {
    const { onUpload, imageOnly, displayedData, setDisplayedData, imageIndex, setImageIndex, cropping, setCropping, aspectRatio } = props;
    const { cropWidth, cropHeight } = imgSizesCalc(aspectRatio);
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const dropRef = useRef(null);
    const fileInputRef = useRef(null);
    const [dragCounter, setDragCounter] = useState();
    const [dragging, setDragging] = useState(false);

    // ======================================
    const [image, setImage] = useState(null);
    const [fileName, setFileName] = useState(null);
    const cropperRef = useRef(null);

    const { setLoading } = useLoading();

    // ======================================

    const handleUpload = (files) => {
        if (imageOnly && files && files.length > 0) {
            setDisplayedData((prev) => [...prev, ...files]);
            setCropping(true);
        } else if (onUpload && typeof onUpload === 'function') onUpload(files);
    };

    useEffect(() => {
        if (displayedData.length > 0) {
            setImageIndex(imageIndex ?? 0);
        }
    }, [displayedData]);

    useEffect(() => {
        if (!isNil(imageIndex) && displayedData.length > 0) {
            setImage(URL.createObjectURL(displayedData[imageIndex]));
            setFileName(displayedData[imageIndex].name);
        }
    }, [imageIndex]);

    const handleUploadCroppedImage = (file) => {
        if (onUpload && typeof onUpload === 'function') onUpload([file]);
    };

    const handleNextPhoto = () => {
        if (imageIndex < displayedData.length - 1) setImageIndex((prev) => prev + 1);
        else setImageIndex(0);
    };

    const handlePreviousPhoto = () => {
        if (imageIndex > 0) setImageIndex((prev) => prev - 1);
        else setImageIndex(displayedData.length - 1);
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setDragCounter((prev) => prev + 1);
        setDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setDragCounter((prev) => prev - 1);
    };

    useEffect(() => {
        if (dragCounter === 0) {
            setDragging(false);
        }
    }, [dragCounter]);

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        setDragging(false);

        //         handleUpload(files);
        //     } else
        //         try {
        //             setLoading(true);
        //             const compressedFile = await imageCompression(files[0], {
        //                 maxSizeMB: 1,
        //                 maxWidthOrHeight: 1024,
        //                 useWebWorker: true
        //             });
        //             const altFiles = [new File([compressedFile], files[0]?.name, { type: files[0]?.type })];
        //             handleUpload(altFiles);
        //             setLoading(false);
        //         } catch {
        //             setLoading(false);
        //             window.alert('Παρουσιάστηκε σφάλμα κατά την μεταφόρτωση αρχείου');
        //         }
        // }

        const files = [...event.dataTransfer.files];
        const filesToCompress = [];
        const filesToUpload = [];
        if (files && files.length > 0) {
            files.forEach(async (file) => {
                console.log(file);
                if (!file.type.startsWith('image')) {
                    if (imageOnly) enqueueSnackbar(`${file.name} ${t('FileNotImage')}`, { variant: 'error' });
                    else if (file.size > 2097152) enqueueSnackbar(`${file.name} ${t('FileTooLarge')}`, { variant: 'error' });
                    else filesToUpload.push(file);
                } else {
                    filesToCompress.push(file);
                    /* try {
                        setLoading(true);
                        const compressedFile = await imageCompression(file, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1024,
                            useWebWorker: true
                        });
                        const altFile = new File([compressedFile], file?.name, { type: file?.type });
                        filesToUpload.push(altFile);
                        setLoading(false);
                    } catch {
                        setLoading(false);
                        enqueueSnackbar(`${file.name} ${t('FileUploadError')}`, { variant: 'error' });
                    } */
                }
            });
        }

        if (filesToCompress.length > 0) {
            setLoading(true);
            filesToCompress.forEach((file) => {
                try {
                    const compressedFile = imageCompression(file, {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1024,
                        useWebWorker: true
                    });
                    const altFile = new File([compressedFile], file?.name, { type: file?.type });
                    filesToUpload.push(altFile);
                } catch {
                    setLoading(false);
                    enqueueSnackbar(`${file.name} ${t('FileUploadError')}`, { variant: 'error' });
                }
            });
            setLoading(false);
        }

        if (filesToUpload.length > 0) handleUpload(filesToUpload);

        /* if (files && files.length > 0) {
            if (!files[0]?.type?.startsWith('image')) {
                if (files[0]?.size > 2 * 1024 * 1024) {
                    window.alert('Μεγάλο Μέγεθος Αρχείου!');
                } // 3MB
                else {
                    enqueueSnackbar(t('FileUploaded'), { variant: 'success' });
                }
            } else if (imageOnly && files.some((file) => !file.type.startsWith('image'))) window.alert('Only images can be uploaded');
            else {
                try {
                    setLoading(true);
                    const compressedFile = await imageCompression(files[0], {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1024,
                        useWebWorker: true
                    });
                    const altFiles = [new File([compressedFile], files[0]?.name, { type: files[0]?.type })];
                    handleUpload(altFiles);
                    setLoading(false);
                } catch {
                    setLoading(false);
                    window.alert('Παρουσιάστηκε σφάλμα κατά την μεταφόρτωση αρχείου');
                }
            }
        } */
    };

    useEffect(() => {
        const instance = dropRef.current;
        if (!cropping) {
            setDragCounter(0);
            instance.addEventListener('dragover', handleDragOver);
            instance.addEventListener('drop', handleDrop);
            instance.addEventListener('dragenter', handleDragEnter);
            instance.addEventListener('dragleave', handleDragLeave);
        }

        return () => {
            if (instance) {
                instance.removeEventListener('dragover', handleDragOver);
                instance.removeEventListener('drop', handleDrop);
                instance.removeEventListener('dragenter', handleDragEnter);
                instance.removeEventListener('dragleave', handleDragLeave);
            }
        };
    }, [cropping]);

    const handleOpenFileExplorer = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    useImperativeHandle(ref, () => ({
        handleSwitchWindow() {
            setCropping((prev) => !prev);
        },
        cropperRef
    }));

    return (
        <>
            {!cropping && (
                <Box
                    ref={dropRef}
                    sx={{
                        display: 'flex',
                        borderWidth: '3px',
                        borderStyle: 'dashed',
                        borderColor: dragging ? '#34eb4c' : 'grey',
                        backgroundColor: dragging ? '#ccfcd3' : '#f3f3f4',
                        borderRadius: 4,
                        flexGrow: 1,
                        height: 'inherit',
                        m: 1
                    }}
                    onMouseEnter={() => {
                        setDragging(false);
                        setDragCounter(0);
                    }}
                    onMouseLeave={() => {
                        setDragging(false);
                        setDragCounter(0);
                    }}
                    justifyContent="center"
                    alignItems="center"
                >
                    {!dragging && (
                        <Stack spacing={2} alignItems="center">
                            <FileUploadOutlinedIcon sx={{ fontSize: 180, color: '#4d79bc' }} />
                            <Typography sx={{ fontSize: 30 }}>{t('DragToUpload')}</Typography>
                            <Typography sx={{ fontSize: 17 }}>{t('Or')}</Typography>
                            <Button
                                variant="contained"
                                sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                                onClick={handleOpenFileExplorer}
                            >
                                <Typography>{t('OpenFileExplorer')}</Typography>
                            </Button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept={imageOnly ? 'image/*' : '*'}
                                style={{ display: 'none' }}
                                multiple="multiple"
                                onChange={async (event) => {
                                    const files = [...event.target.files];
                                    if (files && files.length > 0) {
                                        if (!files[0]?.type?.startsWith('image')) {
                                            if (files[0]?.size > 2 * 1024 * 1024) {
                                                window.alert(t('VeryBigFile'));
                                                return;
                                            }
                                            handleUpload(files);
                                        } else
                                            try {
                                                setLoading(true);
                                                const compressedFile = await imageCompression(files[0], {
                                                    maxSizeMB: 1,
                                                    maxWidthOrHeight: 1024,
                                                    useWebWorker: true
                                                });
                                                const altFiles = [new File([compressedFile], files[0]?.name, { type: files[0]?.type })];
                                                handleUpload(altFiles);
                                                setLoading(false);
                                            } catch {
                                                setLoading(false);
                                                window.alert(t('ErrorUploadingFile'));
                                            }
                                    }
                                    event.target.value = '';
                                }}
                            />
                        </Stack>
                    )}
                    {dragging && (
                        <Stack spacing={2} alignItems="center">
                            <GetAppOutlinedIcon sx={{ fontSize: 180, color: '#34eb4c' }} />
                            <Typography sx={{ fontSize: 30 }}>{t('DropToUpload')}</Typography>
                        </Stack>
                    )}
                </Box>
            )}
            {cropping && (
                <Box
                    sx={{
                        display: 'flex',
                        borderWidth: '3px',
                        borderStyle: 'dashed',
                        borderRadius: 4,
                        flexGrow: 1,
                        height: 'inherit',
                        m: 1
                    }}
                    justifyContent="center"
                    alignItems="center"
                >
                    <Stack
                        spacing={1}
                        hidden={!displayedData.length > 0}
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ flexGrow: 1, height: 'inherit' }}
                    >
                        <Tooltip title={t('PreviousPhoto')}>
                            <IconButton onClick={handlePreviousPhoto} disabled={!displayedData || displayedData?.length < 2}>
                                <ArrowBackIosRoundedIcon sx={{ color: '#4d79bc' }} />
                            </IconButton>
                        </Tooltip>
                        <ImageCrop
                            src={image}
                            srcName={fileName}
                            defaultAR={aspectRatio}
                            savePhoto={handleUploadCroppedImage}
                            minWidth={cropWidth}
                            minHeight={cropHeight}
                            ref={cropperRef}
                        />
                        <Tooltip title={t('NextPhoto')}>
                            <IconButton onClick={handleNextPhoto} disabled={!displayedData || displayedData?.length < 2}>
                                <ArrowForwardIosRoundedIcon sx={{ color: '#4d79bc' }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Box hidden={displayedData.length > 0}>
                        <Typography variant="h4">{t('NoImagesSelected')}</Typography>
                    </Box>
                </Box>
            )}
        </>
    );
});

export default FileExplorerUploadTab;
